/**
 * WebRTC Composable (упрощенная версия)
 * Координирует работу PeerConnection и использует модульные composables
 *
 * Отвечает за:
 * - Создание и управление RTCPeerConnection
 * - Создание и обработку Offer/Answer
 * - Обработку ICE candidates
 * - Переподключение при разрыве соединения
 */

import { ref, onUnmounted, type Ref } from 'vue';
import { useMediaDevices } from './useMediaDevices';
import { useQualityControl } from './useQualityControl';
import { useConnectionStats } from './useConnectionStats';
import { useDataChannel } from './useDataChannel';
import type { ConnectionState } from '../types';
import { fetchTurnCredentials } from '../utils/turnCredentials';
import { STATS_INTERVAL, QUALITY_ADAPTATION_INTERVAL } from '../constants';

export function useWebRTC() {
  // ==================== ИСПОЛЬЗОВАНИЕ COMPOSABLES ====================

  /** Управление медиа устройствами */
  const mediaDevices = useMediaDevices();

  /** Управление качеством видео */
  const qualityControl = useQualityControl();

  /** Сбор статистики соединения */
  const connectionStats = useConnectionStats();

  /** Управление DataChannel */
  const dataChannelManager = useDataChannel();

  // ==================== СОСТОЯНИЕ PEERCONNECTION ====================

  /** RTCPeerConnection */
  const peerConnection: Ref<RTCPeerConnection | null> = ref(null);

  /** Состояние соединения */
  const connectionState: Ref<ConnectionState> = ref('new');

  /** Флаг переподключения */
  const isReconnecting: Ref<boolean> = ref(false);

  /** ICE серверы (TURN/STUN) */
  let iceServers: RTCIceServer[] = [];

  /** Интервалы */
  let statsInterval: ReturnType<typeof setInterval> | null = null;
  let qualityAdaptationInterval: ReturnType<typeof setInterval> | null = null;

  /** Детектор смены сети */
  let networkChangeDetector: (() => void) | null = null;

  // ==================== КОЛБЭКИ ДЛЯ СИГНАЛИНГА ====================

  /** Колбэк для локальных ICE candidates */
  let localIceCandidateCallback: ((candidate: RTCIceCandidateInit) => void) | null = null;

  /** Колбэк для локального Offer */
  let localOfferCallback: ((offer: RTCSessionDescriptionInit) => void) | null = null;

  /** Колбэк для ICE restart */
  let iceRestartCallback: ((offer: RTCSessionDescriptionInit) => void) | null = null;

  // ==================== ИНИЦИАЛИЗАЦИЯ ====================

  /**
   * Инициализация WebRTC соединения
   * Получает TURN credentials и инициализирует медиа потоки
   */
  async function initWebRTC(token: string): Promise<boolean> {
    try {
      // Получаем TURN credentials с сервера через утилиту
      iceServers = await fetchTurnCredentials(token);
      console.log('✅ ICE Servers получены:', iceServers);

      // Получаем медиа поток
      const stream = await getUserMedia();
      if (!stream) {
        console.warn('⚠️ Не удалось получить доступ к медиа, продолжаем без медиа...');
        return false;
      }

      // Определяем возможности устройства
      await qualityControl.detectDeviceCapabilities(stream);

      // Создаем PeerConnection
      try {
        createPeerConnection();
        console.log('✅ PeerConnection создано');

        // Включаем отслеживание смены сети
        setupNetworkChangeDetection();
        console.log('✅ Детектор смены сети включен');
      } catch (e) {
        console.error('❌ Ошибка создания PeerConnection:', e);
        return false;
      }

      console.log('✅ WebRTC инициализация завершена успешно');
      return true;

    } catch (error) {
      console.error('❌ Не удалось инициализировать WebRTC:', error);
      return false;
    }
  }

  /**
   * Получает медиа поток с камеры и микрофона
   */
  async function getUserMedia(): Promise<MediaStream | null> {
    try {
      console.log('🎥 Запрос доступа к медиа устройствам...');

      // Определяем доступные устройства
      await mediaDevices.detectAvailableCameras();
      await mediaDevices.detectAudioDevices();

      // Получаем поток с выбранной камерой и качеством
      const quality = qualityControl.qualityProfiles[qualityControl.currentQuality.value];
      const stream = await mediaDevices.getStreamWithCamera(
        mediaDevices.cameraInfo.value.currentCamera?.deviceId,
        quality
      );

      // Устанавливаем флаги доступности
      mediaDevices.hasVideo.value = stream.getVideoTracks().length > 0;
      mediaDevices.hasAudio.value = stream.getAudioTracks().length > 0;

      console.log('✅ Медиа поток получен:', {
        video: mediaDevices.hasVideo.value,
        audio: mediaDevices.hasAudio.value
      });

      return stream;

    } catch (error) {
      console.error('❌ Ошибка получения медиа:', error);

      // Пробуем получить хотя бы аудио
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaDevices.localStream.value = audioStream;
        mediaDevices.hasAudio.value = true;
        mediaDevices.hasVideo.value = false;
        console.log('⚠️ Получен только аудио поток');
        return audioStream;
      } catch (audioError) {
        console.error('❌ Не удалось получить даже аудио:', audioError);
        return null;
      }
    }
  }

  // ==================== УПРАВЛЕНИЕ PEERCONNECTION ====================

  /**
   * Создает RTCPeerConnection с настройками
   */
  function createPeerConnection(): void {
    const config: RTCConfiguration = {
      iceServers: iceServers,
      iceTransportPolicy: 'all',
      bundlePolicy: 'max-bundle',
      rtcpMuxPolicy: 'require'
    };

    peerConnection.value = new RTCPeerConnection(config);

    // Инициализируем DataChannel
    dataChannelManager.initializeDataChannel(peerConnection.value);

    // Добавляем локальные треки
    if (mediaDevices.localStream.value) {
      mediaDevices.localStream.value.getTracks().forEach(track => {
        if (peerConnection.value && mediaDevices.localStream.value) {
          peerConnection.value.addTrack(track, mediaDevices.localStream.value);
        }
      });
    }

    // Обработчик ICE candidates
    peerConnection.value.onicecandidate = (event) => {
      if (event.candidate && localIceCandidateCallback) {
        localIceCandidateCallback(event.candidate.toJSON());
      }
    };

    // Обработчик изменения состояния соединения
    peerConnection.value.onconnectionstatechange = () => {
      if (!peerConnection.value) return;

      const state = peerConnection.value.connectionState;
      connectionState.value = state as ConnectionState;
      console.log('🔗 Connection state changed:', state);

      if (state === 'connected') {
        isReconnecting.value = false;
        startStatsCollection();
        startQualityAdaptation();
      } else if (state === 'disconnected' || state === 'failed') {
        handleDisconnection();
      }
    };

    // Обработчик удаленного потока
    peerConnection.value.ontrack = (event: RTCTrackEvent) => {
      console.log('📥 Remote track received:', event.track.kind);
      if (event.streams && event.streams[0]) {
        mediaDevices.remoteStream.value = event.streams[0];

        // Слушаем события mute/unmute на удаленных треках
        const track = event.track;
        
        track.onmute = () => {
          console.log(`🔇 Remote ${track.kind} muted`);
          dataChannelManager.updateRemoteMediaFromTrackMute(track, true);
        };

        track.onunmute = () => {
          console.log(`🔊 Remote ${track.kind} unmuted`);
          dataChannelManager.updateRemoteMediaFromTrackMute(track, false);
        };
      }
    };

    console.log('✅ PeerConnection создано с конфигурацией:', config);
  }

  /**
   * Создает Offer для инициации соединения
   */
  async function createOffer(): Promise<RTCSessionDescriptionInit> {
    if (!peerConnection.value) {
      throw new Error('PeerConnection не создано');
    }

    const offer = await peerConnection.value.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });

    await peerConnection.value.setLocalDescription(offer);
    console.log('✅ Offer создан');

    return offer;
  }

  /**
   * Обрабатывает входящий ICE candidate
   */
  async function handleIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!peerConnection.value) {
      console.warn('⚠️ PeerConnection не готово для ICE candidate');
      return;
    }

    try {
      await peerConnection.value.addIceCandidate(new RTCIceCandidate(candidate));
      console.log('✅ ICE candidate добавлен');
    } catch (error) {
      console.error('❌ Ошибка добавления ICE candidate:', error);
    }
  }

  // ==================== ПЕРЕПОДКЛЮЧЕНИЕ ====================

  /**
   * Обрабатывает разрыв соединения
   */
  function handleDisconnection(): void {
    if (isReconnecting.value) return;

    console.log('🔌 Соединение потеряно, пытаемся переподключиться...');
    isReconnecting.value = true;

    // Даем время на восстановление
    setTimeout(() => {
      if (connectionState.value === 'disconnected' || connectionState.value === 'failed') {
        restartIce();
      }
    }, 2000);
  }

  /**
   * Перезапускает ICE для переподключения
   */
  async function restartIce(): Promise<void> {
    if (!peerConnection.value) return;

    try {
      console.log('🔄 Перезапуск ICE...');
      const offer = await peerConnection.value.createOffer({ iceRestart: true });
      await peerConnection.value.setLocalDescription(offer);

      if (iceRestartCallback) {
        iceRestartCallback(offer);
      }

      console.log('✅ ICE restart инициирован');
    } catch (error) {
      console.error('❌ Ошибка ICE restart:', error);
    }
  }

  /**
   * Полное переподключение
   */
  async function reconnect(): Promise<void> {
    console.log('🔄 Полное переподключение...');

    // Останавливаем интервалы
    stopStatsCollection();
    stopQualityAdaptation();

    // Закрываем старое соединение
    if (peerConnection.value) {
      peerConnection.value.close();
      peerConnection.value = null;
    }

    // Пересоздаем соединение
    createPeerConnection();

    // Создаем новый offer
    if (localOfferCallback) {
      const offer = await createOffer();
      localOfferCallback(offer);
    }

    console.log('✅ Переподключение завершено');
  }

  // ==================== СТАТИСТИКА И КАЧЕСТВО ====================

  /**
   * Запускает сбор статистики
   */
  function startStatsCollection(): void {
    if (statsInterval) return;

    statsInterval = setInterval(async () => {
      await connectionStats.collectStats(peerConnection.value);
    }, STATS_INTERVAL);
  }

  /**
   * Останавливает сбор статистики
   */
  function stopStatsCollection(): void {
    if (statsInterval) {
      clearInterval(statsInterval);
      statsInterval = null;
    }
  }

  /**
   * Запускает адаптацию качества
   */
  function startQualityAdaptation(): void {
    if (qualityAdaptationInterval) return;

    qualityAdaptationInterval = setInterval(() => {
      qualityControl.adaptQuality(connectionStats.stats.value);

      // Применяем новое качество если изменилось
      const newQuality = qualityControl.currentQuality.value;
      qualityControl.applyQualitySafely(mediaDevices.localStream.value, newQuality);
    }, QUALITY_ADAPTATION_INTERVAL);
  }

  /**
   * Останавливает адаптацию качества
   */
  function stopQualityAdaptation(): void {
    if (qualityAdaptationInterval) {
      clearInterval(qualityAdaptationInterval);
      qualityAdaptationInterval = null;
    }
  }

  // ==================== ДЕТЕКТОР СМЕНЫ СЕТИ ====================

  /**
   * Настраивает детектор смены сети
   */
  function setupNetworkChangeDetection(): void {
    if (!('connection' in navigator)) return;

    const connection = (navigator as any).connection;
    if (!connection) return;

    const handleNetworkChange = () => {
      console.log('🌐 Сеть изменилась, перезапускаем ICE...');
      restartIce();
    };

    connection.addEventListener('change', handleNetworkChange);
    networkChangeDetector = () => {
      connection.removeEventListener('change', handleNetworkChange);
    };
  }

  // ==================== КОЛБЭКИ ДЛЯ СИГНАЛИНГА ====================

  /**
   * Устанавливает колбэк для локальных ICE candidates
   */
  function onLocalIceCandidate(callback: (candidate: RTCIceCandidateInit) => void): void {
    localIceCandidateCallback = callback;
  }

  /**
   * Устанавливает колбэк для локального Offer
   */
  function onLocalOffer(callback: (offer: RTCSessionDescriptionInit) => void): void {
    localOfferCallback = callback;
  }

  /**
   * Устанавливает колбэк для ICE restart
   */
  function onIceRestart(callback: (offer: RTCSessionDescriptionInit) => void): void {
    iceRestartCallback = callback;
  }

  /**
   * Устанавливает колбэк для сообщений из dataChannel
   */
  function onDataChannelMessage(callback: (message: { type: string; payload: any }) => void): void {
    dataChannelManager.onMessage(callback);
  }

  /**
   * Отправляет состояние медиа через dataChannel
   */
  function sendMediaState(videoMuted?: boolean, audioMuted?: boolean): void {
    dataChannelManager.sendMediaState(videoMuted, audioMuted);
  }

  /**
   * Включает/выключает видео трек с двойным контуром (локально + sender + dataChannel)
   */
  async function toggleVideoTrack(enabled: boolean): Promise<void> {
    // 1. Локально: включаем/выключаем трек
    mediaDevices.toggleVideoTrack(enabled);

    // 3. Отправляем состояние через dataChannel
    sendMediaState(!enabled, undefined);
  }

  /**
   * Включает/выключает аудио трек с двойным контуром (локально + sender + dataChannel)
   */
  async function toggleAudioTrack(enabled: boolean): Promise<void> {
    // 1. Локально: включаем/выключаем трек

    mediaDevices.toggleAudioTrack(enabled);

    // 3. Отправляем состояние через dataChannel
    sendMediaState(undefined, !enabled);
  }

  // ==================== ОЧИСТКА ====================

  /**
   * Очищает ресурсы
   */
  function cleanup(): void {
    stopStatsCollection();
    stopQualityAdaptation();

    if (networkChangeDetector) {
      networkChangeDetector();
    }

    if (peerConnection.value) {
      peerConnection.value.close();
    }

    mediaDevices.cleanup();
    dataChannelManager.cleanup();
  }

  // Автоматическая очистка при размонтировании
  onUnmounted(() => {
    cleanup();
  });

  // ==================== ДЕЛЕГИРОВАНИЕ К COMPOSABLES ====================

  /**
   * Заменяет трек в PeerConnection (для переключения устройств)
   */
  async function replaceSenderTrack(kind: 'audio' | 'video', track: MediaStreamTrack): Promise<boolean> {
    if (!peerConnection.value) return false;
    console.log('🔄 Заменяем трек:', kind, track);

    const sender = peerConnection.value
      .getSenders()
      .find(s => s.track?.kind === kind);
    console.log('🔄 Найден sender:', sender);

    if (sender) {
      console.log('🔄 Трек заменен:', sender);
      await sender.replaceTrack(track);
      return true;
    }

    console.log('🔄 Трек не заменен:', sender);
    return false;
  }

  // ==================== ВОЗВРАЩАЕМЫЙ API ====================

  return {
    // Состояние
    peerConnection,
    dataChannel: dataChannelManager.dataChannel,
    connectionState,
    isReconnecting,
    remoteVideoMuted: dataChannelManager.remoteVideoMuted,
    remoteAudioMuted: dataChannelManager.remoteAudioMuted,

    // Медиа (делегирование к useMediaDevices)
    localStream: mediaDevices.localStream,
    remoteStream: mediaDevices.remoteStream,
    hasVideo: mediaDevices.hasVideo,
    hasAudio: mediaDevices.hasAudio,
    cameraInfo: mediaDevices.cameraInfo,
    audioDeviceInfo: mediaDevices.audioDeviceInfo,

    // Качество (делегирование к useQualityControl)
    currentQuality: qualityControl.currentQuality,
    deviceCapabilities: qualityControl.deviceCapabilities,
    qualityProfiles: qualityControl.qualityProfiles,
    isQualitySupported: qualityControl.isQualitySupported,
    applyQualitySafely: (quality: string) =>
      qualityControl.applyQualitySafely(mediaDevices.localStream.value, quality as any),

    // Статистика (делегирование к useConnectionStats)
    stats: connectionStats.stats,
    connectionType: connectionStats.connectionType,
    getPeerMetrics: () =>
      connectionStats.getPeerMetrics(
        qualityControl.currentQuality.value,
        mediaDevices.cameraInfo.value,
        mediaDevices.audioDeviceInfo.value,
        mediaDevices.localStream.value
      ),

    // Инициализация
    initWebRTC,
    createOffer,
    handleIceCandidate,

    // Управление устройствами
    // Используем новые функции с двойным контуром (sender + dataChannel)
    toggleAudioTrack,
    toggleVideoTrack,
    switchCamera: () => mediaDevices.switchCamera(replaceSenderTrack),
    switchToSpecificCamera: (camera: any) => mediaDevices.switchToSpecificCamera(camera, replaceSenderTrack),
    switchAudioInput: (deviceId: string) => mediaDevices.switchAudioInput(deviceId, replaceSenderTrack),
    switchAudioOutput: mediaDevices.switchAudioOutput,

    // Переподключение
    reconnect,
    setRemoteStream: (stream: MediaStream | null) => {
      mediaDevices.remoteStream.value = stream;
    },

    // Колбэки
    onLocalIceCandidate,
    onLocalOffer,
    onIceRestart,
    onDataChannelMessage,

    // Очистка
    cleanup
  };
}
